import React, { useState, useMemo, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Card } from '@/components/ui/card';
import { Check, ChevronsUpDown, Plus, Search, Edit2, Trash2, Package } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MaterialCache, type Material } from '@/services/materialCache';
import { useToast } from '@/hooks/use-toast';

interface DialogAddMaterialProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (materialData: any) => void;
}

interface AddMaterialModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
}

interface MaterialListItem {
  id: string;
  material: string;
  unit: string;
  quantity: number;
  materialType: 'received' | 'used';
  supplier?: string;
  document?: string;
  time?: string;
  status?: "conforme" | "aguardando_inspecao" | "recusado";
  location?: string;
  observation?: string;
}

const AddMaterialModal: React.FC<AddMaterialModalProps> = ({ open, onOpenChange, onSave }) => {
  const [materialData, setMaterialData] = useState({
    nome: '',
    categoria: '',
    unidade: ''
  });
  const { toast } = useToast();

  const handleSave = () => {
    if (!materialData.nome || !materialData.unidade) {
      toast({
        title: "Erro",
        description: "Nome do material e unidade são obrigatórios.",
        variant: "destructive"
      });
      return;
    }

    MaterialCache.addSessionMaterial({
      nome: materialData.nome,
      categoria: materialData.categoria || 'Outros',
      unidade: materialData.unidade,
      status: 'Ativo',
      sinonimos: [],
      codigo: ''
    });

    setMaterialData({
      nome: '',
      categoria: '',
      unidade: ''
    });

    toast({
      title: "Sucesso",
      description: "Material adicionado com sucesso!"
    });

    onSave();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Cadastrar Novo Material</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="nome">Nome do Material *</Label>
            <Input
              id="nome"
              value={materialData.nome}
              onChange={(e) => setMaterialData(prev => ({ ...prev, nome: e.target.value }))}
              placeholder="Ex: Cimento CP-II"
            />
          </div>
          <div>
            <Label htmlFor="categoria">Categoria</Label>
            <Input
              id="categoria"
              value={materialData.categoria}
              onChange={(e) => setMaterialData(prev => ({ ...prev, categoria: e.target.value }))}
              placeholder="Ex: Estrutura"
            />
          </div>
          <div>
            <Label htmlFor="unidade">Unidade *</Label>
            <Input
              id="unidade"
              value={materialData.unidade}
              onChange={(e) => setMaterialData(prev => ({ ...prev, unidade: e.target.value }))}
              placeholder="Ex: m³, kg, sc"
            />
          </div>
        </div>

        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave}>
            Salvar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const DialogAddMaterial: React.FC<DialogAddMaterialProps> = ({
  open,
  onOpenChange,
  onAdd
}) => {
  const [materialType, setMaterialType] = useState<'received' | 'used'>('received');
  const [selectedMaterial, setSelectedMaterial] = useState("");
  const [comboOpen, setComboOpen] = useState(false);
  const [addMaterialModalOpen, setAddMaterialModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const { toast } = useToast();

  // Pending items state
  const [pendingItems, setPendingItems] = useState<MaterialListItem[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // Form states
  const [materialData, setMaterialData] = useState({
    material: "",
    quantity: 0,
    unit: "",
    observation: "",
    // Received specific
    document: "",
    supplier: "",
    time: "",
    status: "conforme" as "conforme" | "aguardando_inspecao" | "recusado",
    // Used specific
    location: ""
  });

  // Get available materials
  const allMaterials = useMemo(() => MaterialCache.getAllAvailableMaterials(), [refreshKey]);

  // Build dropdown items
  const dropdownItems = useMemo(() => {
    const items: Array<{
      id: string;
      nome: string;
      type: 'action' | 'separator' | 'material';
      unidade: string;
      categoria?: string;
      codigo?: string;
      sinonimos?: string[];
    }> = [
      {
        id: 'add-new',
        nome: 'Cadastrar Novo Material',
        type: 'action',
        unidade: ''
      },
      {
        id: 'import-system',
        nome: 'Importar material do sistema',
        type: 'action',
        unidade: ''
      }
    ];

    // Add separator and materials
    if (allMaterials.length > 0) {
      items.push({
        id: 'separator',
        nome: '--- Materiais Disponíveis ---',
        type: 'separator',
        unidade: ''
      });

      allMaterials.forEach(material => {
        items.push({
          id: material.id,
          nome: material.nome,
          type: 'material',
          unidade: material.unidade,
          categoria: material.categoria,
          codigo: material.codigo,
          sinonimos: material.sinonimos
        });
      });
    }

    return items;
  }, [allMaterials]);

  // Filter items by search term
  const filteredItems = useMemo(() => {
    if (!searchTerm) return dropdownItems;
    
    return dropdownItems.filter(item => {
      if (item.type === 'action' || item.type === 'separator') return true;
      
      const term = searchTerm.toLowerCase();
      return item.nome.toLowerCase().includes(term) ||
             item.codigo?.toLowerCase().includes(term) ||
             item.categoria?.toLowerCase().includes(term) ||
             item.sinonimos?.some(s => s.toLowerCase().includes(term));
    });
  }, [dropdownItems, searchTerm]);

  const handleMaterialSelect = (itemId: string) => {
    if (itemId === 'add-new') {
      setAddMaterialModalOpen(true);
      return;
    }

    if (itemId === 'import-system') {
      // Show import dialog with catalog materials
      toast({
        title: "Info",
        description: "Funcionalidade de importação em desenvolvimento"
      });
      return;
    }

    if (itemId === 'separator') return;

    const material = allMaterials.find(m => m.id === itemId);
    if (material) {
      setMaterialData(prev => ({
        ...prev,
        material: material.nome,
        unit: material.unidade
      }));
    }
    setSelectedMaterial(itemId);
    setComboOpen(false);
  };

  const addToPendingList = () => {
    if (!materialData.material || materialData.quantity <= 0) {
      toast({
        title: "Erro",
        description: "Material e quantidade são obrigatórios.",
        variant: "destructive"
      });
      return;
    }

    const materialItem: MaterialListItem = {
      id: editingIndex !== null ? pendingItems[editingIndex].id : crypto.randomUUID(),
      material: materialData.material,
      unit: materialData.unit,
      quantity: materialData.quantity,
      materialType: materialType,
      observation: materialData.observation,
      ...(materialType === 'received' && {
        supplier: materialData.supplier,
        document: materialData.document,
        time: materialData.time,
        status: materialData.status
      }),
      ...(materialType === 'used' && {
        location: materialData.location
      })
    };

    if (editingIndex !== null) {
      // Update existing item
      const newPendingItems = [...pendingItems];
      newPendingItems[editingIndex] = materialItem;
      setPendingItems(newPendingItems);
      setEditingIndex(null);
    } else {
      // Add new item
      setPendingItems(prev => [...prev, materialItem]);
    }

    // Reset form
    setSelectedMaterial("");
    setMaterialData({
      material: "",
      quantity: 0,
      unit: "",
      observation: "",
      document: "",
      supplier: "",
      time: "",
      status: "conforme",
      location: ""
    });

    toast({
      title: "Sucesso",
      description: editingIndex !== null ? "Item atualizado na lista!" : "Item adicionado à lista!"
    });
  };

  const editPendingItem = (index: number) => {
    const item = pendingItems[index];
    
    // Find the material in available materials
    const material = allMaterials.find(m => m.nome === item.material);
    if (material) {
      setSelectedMaterial(material.id);
    }
    
    setMaterialData({
      material: item.material,
      unit: item.unit,
      quantity: item.quantity,
      observation: item.observation || "",
      document: item.document || "",
      supplier: item.supplier || "",
      time: item.time || "",
      status: item.status || "conforme",
      location: item.location || ""
    });
    
    setMaterialType(item.materialType);
    setEditingIndex(index);
  };

  const removePendingItem = (index: number) => {
    setPendingItems(prev => prev.filter((_, i) => i !== index));
    toast({
      title: "Sucesso",
      description: "Item removido da lista!"
    });
  };

  const confirmAllItems = () => {
    if (pendingItems.length === 0) {
      toast({
        title: "Erro",
        description: "Adicione pelo menos um item à lista.",
        variant: "destructive"
      });
      return;
    }

    // Add all pending items
    pendingItems.forEach(item => {
      const { id, materialType, ...itemData } = item;
      
      // Format data correctly for handleAddMaterial which expects { type, ...data }
      const materialData = {
        type: materialType, // 'received' or 'used'
        ...itemData,
        observations: itemData.observation // Map observation to observations for consistency
      };
      
      onAdd(materialData);
    });

    // Reset everything
    setPendingItems([]);
    setSelectedMaterial("");
    setMaterialData({
      material: "",
      quantity: 0,
      unit: "",
      observation: "",
      document: "",
      supplier: "",
      time: "",
      status: "conforme",
      location: ""
    });
    onOpenChange(false);

    toast({
      title: "Sucesso",
      description: `${pendingItems.length} item(s) adicionado(s) com sucesso!`
    });
  };

  const clearList = () => {
    setPendingItems([]);
    setEditingIndex(null);
    toast({
      title: "Info",
      description: "Lista limpa!"
    });
  };

  // Initialize form when dialog opens
  useEffect(() => {
    if (open) {
      setPendingItems([]);
      setEditingIndex(null);
      setSelectedMaterial("");
      setMaterialData({
        material: "",
        quantity: 0,
        unit: "",
        observation: "",
        document: "",
        supplier: "",
        time: "",
        status: "conforme",
        location: ""
      });
    }
  }, [open]);

  return (
    <>
      <AddMaterialModal
        open={addMaterialModalOpen}
        onOpenChange={setAddMaterialModalOpen}
        onSave={() => setRefreshKey(prev => prev + 1)}
      />

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Adicionar Material</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Material Type Selection */}
            <div>
              <Label className="text-sm font-medium">Tipo de Lançamento</Label>
              <RadioGroup
                value={materialType}
                onValueChange={(value) => setMaterialType(value as 'received' | 'used')}
                className="flex flex-row space-x-6 mt-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="received" id="received" />
                  <Label htmlFor="received">Material Recebido</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="used" id="used" />
                  <Label htmlFor="used">Material Utilizado</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Material Selection */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Material</Label>
                <span className="text-xs text-muted-foreground">
                  Materiais disponíveis ({allMaterials.length})
                </span>
              </div>
              <Popover open={comboOpen} onOpenChange={setComboOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={comboOpen}
                    className="w-full justify-between"
                  >
                    {materialData.material || "Selecione um material..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0 z-[60]" align="start">
                  <Command>
                    <CommandInput 
                      placeholder="Digite para buscar material..." 
                      value={searchTerm}
                      onValueChange={setSearchTerm}
                    />
                    <CommandList>
                      <CommandEmpty>Nenhum material encontrado.</CommandEmpty>
                      <CommandGroup>
                        {filteredItems.map((item) => {
                          if (item.type === 'action') {
                            return (
                              <CommandItem
                                key={item.id}
                                value={item.nome}
                                onSelect={() => handleMaterialSelect(item.id)}
                                className="flex items-center gap-2 font-medium text-primary"
                              >
                                {item.id === 'add-new' ? <Plus className="h-4 w-4" /> : <Search className="h-4 w-4" />}
                                {item.nome}
                              </CommandItem>
                            );
                          }

                          if (item.type === 'separator') {
                            return (
                              <div key={item.id}>
                                <CommandSeparator className="my-2" />
                                <div className="px-2 py-1 text-xs font-medium text-muted-foreground">
                                  {item.nome}
                                </div>
                              </div>
                            );
                          }

                          return (
                            <CommandItem
                              key={item.id}
                              value={`${item.nome} ${item.codigo || ''} ${item.categoria || ''} ${item.sinonimos?.join(' ') || ''}`}
                              onSelect={() => handleMaterialSelect(item.id)}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  selectedMaterial === item.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <Package className="mr-2 h-4 w-4 text-muted-foreground" />
                              <div className="flex-1">
                                <div className="font-medium">{item.nome}</div>
                                <div className="text-sm text-muted-foreground flex gap-2">
                                  {item.codigo && <span>Cód: {item.codigo}</span>}
                                  <span>Unidade: {item.unidade}</span>
                                  {item.categoria && <span>Categoria: {item.categoria}</span>}
                                </div>
                                {item.sinonimos && item.sinonimos.length > 0 && (
                                  <div className="text-xs text-muted-foreground">
                                    Sinônimos: {item.sinonimos.join(', ')}
                                  </div>
                                )}
                              </div>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Common Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="quantity">Quantidade *</Label>
                <div className="relative">
                  <Input
                    id="quantity"
                    type="number"
                    min="0"
                    step="1"
                    value={materialData.quantity || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow empty string during typing
                      if (value === '') {
                        setMaterialData(prev => ({ ...prev, quantity: 0 }));
                        return;
                      }
                      // Parse and set the value (allows decimals during manual typing)
                      const numValue = parseFloat(value);
                      if (!isNaN(numValue)) {
                        setMaterialData(prev => ({ ...prev, quantity: numValue }));
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && materialData.material && materialData.quantity > 0) {
                        addToPendingList();
                      }
                    }}
                    onFocus={(e) => {
                      // When focused, set step to 1 for spinner controls
                      e.target.step = "1";
                    }}
                    onBlur={(e) => {
                      // When blurred, allow decimals but keep step as 1 for next time
                      e.target.step = "1";
                    }}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="unit">Unidade</Label>
                <Input
                  id="unit"
                  value={materialData.unit}
                  onChange={(e) => setMaterialData(prev => ({ ...prev, unit: e.target.value }))}
                  placeholder="Ex: m³, kg, sc"
                  readOnly
                />
              </div>
            </div>

            {/* Type-specific fields */}
            {materialType === 'received' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="supplier">Fornecedor</Label>
                    <Input
                      id="supplier"
                      value={materialData.supplier}
                      onChange={(e) => setMaterialData(prev => ({ ...prev, supplier: e.target.value }))}
                      placeholder="Nome do fornecedor"
                    />
                  </div>
                  <div>
                    <Label htmlFor="document">Documento</Label>
                    <Input
                      id="document"
                      value={materialData.document}
                      onChange={(e) => setMaterialData(prev => ({ ...prev, document: e.target.value }))}
                      placeholder="Nota fiscal, etc."
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="time">Horário de Recebimento</Label>
                    <Input
                      id="time"
                      type="time"
                      value={materialData.time}
                      onChange={(e) => setMaterialData(prev => ({ ...prev, time: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="status">Status</Label>
                    <Select
                      value={materialData.status}
                      onValueChange={(value: "conforme" | "aguardando_inspecao" | "recusado") => 
                        setMaterialData(prev => ({ ...prev, status: value }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="conforme">Conforme</SelectItem>
                        <SelectItem value="aguardando_inspecao">Aguardando Inspeção</SelectItem>
                        <SelectItem value="recusado">Recusado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            )}

            {materialType === 'used' && (
              <div>
                <Label htmlFor="location">Local de Utilização</Label>
                <Input
                  id="location"
                  value={materialData.location}
                  onChange={(e) => setMaterialData(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="Ex: Bloco A - 2º andar"
                />
              </div>
            )}

            <div>
              <Label htmlFor="observation">Observações</Label>
              <Textarea
                id="observation"
                value={materialData.observation}
                onChange={(e) => setMaterialData(prev => ({ ...prev, observation: e.target.value }))}
                placeholder="Observações adicionais..."
                rows={3}
              />
            </div>

            {/* Add to List Button */}
            <div className="flex justify-center">
              <Button 
                onClick={addToPendingList}
                disabled={!materialData.material || materialData.quantity <= 0}
                className="w-full sm:w-auto"
              >
                {editingIndex !== null ? "Atualizar na Lista" : "Adicionar à Lista"}
              </Button>
            </div>

            {/* Pending Items List */}
            {pendingItems.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">Itens a Confirmar ({pendingItems.length})</h4>
                  <Button variant="outline" size="sm" onClick={clearList}>
                    Limpar Lista
                  </Button>
                </div>
                
                <div className="space-y-2">
                  {pendingItems.map((item, index) => (
                    <Card key={item.id} className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-medium">{item.material}</span>
                            <span className="text-sm text-muted-foreground">
                              • {item.quantity} {item.unit}
                            </span>
                            <span className={cn(
                              "text-xs px-2 py-1 rounded",
                              item.materialType === 'received' 
                                ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                                : "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300"
                            )}>
                              {item.materialType === 'received' ? 'Recebido' : 'Utilizado'}
                            </span>
                          </div>
                          
                          <div className="text-sm text-muted-foreground">
                            {item.materialType === 'received' && (
                              <div className="flex gap-4">
                                {item.supplier && <span>Fornecedor: {item.supplier}</span>}
                                {item.document && <span>Doc: {item.document}</span>}
                                {item.time && <span>Horário: {item.time}</span>}
                                {item.status && (
                                  <span className={cn(
                                    item.status === 'conforme' && "text-green-600",
                                    item.status === 'aguardando_inspecao' && "text-yellow-600",
                                    item.status === 'recusado' && "text-red-600"
                                  )}>
                                    Status: {item.status === 'conforme' ? 'Conforme' : 
                                           item.status === 'aguardando_inspecao' ? 'Aguardando Inspeção' : 'Recusado'}
                                  </span>
                                )}
                              </div>
                            )}
                            
                            {item.materialType === 'used' && item.location && (
                              <span>Local: {item.location}</span>
                            )}
                            
                            {item.observation && (
                              <div className="mt-1">Obs: {item.observation}</div>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => editPendingItem(index)}
                            disabled={editingIndex !== null}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => removePendingItem(index)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer Buttons */}
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            
            <div className="flex gap-2">
              {pendingItems.length > 0 && (
                <Button variant="outline" onClick={clearList}>
                  Limpar Lista
                </Button>
              )}
              <Button 
                onClick={confirmAllItems}
                disabled={pendingItems.length === 0}
              >
                Confirmar {pendingItems.length > 0 ? `(${pendingItems.length})` : ''}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};